import React from 'react'
import { FileText, Share2, GitBranch, AlertCircle, Upload, LifeBuoy } from 'lucide-react'
import type { Notification } from '../types'

// Support-ticket notification types are emitted by the backend as `support_<event>`
// (support/notifications.py → notify.send_notification(..., `support_${event_type}`))
// with resource_id = the ticket_id and link = /support?ticket={id}. Registered
// here as EXPLICIT nav cases (never the resource_id-guessing default branch) —
// the specific regression class BUG-102/S2-02 already hit once.
export const SUPPORT_NOTIFICATION_TYPES = [
  'support_ticket_created',
  'support_requester_message',
  'support_admin_reply',
  'support_ticket_closed',
  'support_ticket_reopened',
  'support_ticket_escalated',
  'support_attachment_scan_result',
  'support_quarantine_released',
  'support_attachment_quarantined',
] as const

export const TYPE_LABELS: Record<string, string> = {
  review_request: 'Review Request',
  review_completed: 'Review Completed',
  permission_granted: 'Permission Granted',
  connection_request: 'Connection Request',
  connection_response: 'Connection Response',
  system: 'System Alert',
  upload: 'Document Uploaded',
  disclosure_advance_request: 'Disclosure Request',
  disclosure_consent: 'Disclosure Consent',
  disclosure_advance_declined: 'Disclosure Request Declined',
  disclosure_revoked: 'Disclosure Revoked',
  smpc_verification: 'Similarity Verification',
  crosspollination_interest: 'Cross-Pollination Interest',
  support_ticket_created: 'Support Ticket Received',
  support_requester_message: 'New Support Reply',
  support_admin_reply: 'Support Replied',
  support_ticket_closed: 'Support Ticket Closed',
  support_ticket_reopened: 'Support Ticket Reopened',
  support_ticket_escalated: 'Support Ticket Escalated',
  support_attachment_scan_result: 'Attachment Scan Finished',
  support_quarantine_released: 'Attachment Cleared',
  support_attachment_quarantined: 'Attachment Quarantined',
}

/**
 * Human-readable label for a notification type. Falls back to Title-Cased words
 * (never the raw snake_case event key) so a new backend type can never surface
 * as e.g. "disclosure_advance_request" in the UI again (S2-06).
 */
export function notificationLabel(type: string | undefined | null): string {
  if (!type) return 'Notification'
  return (
    TYPE_LABELS[type] ??
    type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  )
}

export const TYPE_ICONS: Record<string, React.ReactNode> = {
  review_request: React.createElement(FileText, { size: 16, className: 'text-purple-500' }),
  review_completed: React.createElement(FileText, { size: 16, className: 'text-green-500' }),
  permission_granted: React.createElement(Share2, { size: 16, className: 'text-iflow-500' }),
  connection_request: React.createElement(GitBranch, { size: 16, className: 'text-green-500' }),
  connection_response: React.createElement(GitBranch, { size: 16, className: 'text-iflow-500' }),
  system: React.createElement(AlertCircle, { size: 16, className: 'text-yellow-500' }),
  upload: React.createElement(Upload, { size: 16, className: 'text-gray-500' }),
  disclosure_advance_request: React.createElement(Share2, { size: 16, className: 'text-iflow-500' }),
  disclosure_consent: React.createElement(Share2, { size: 16, className: 'text-green-500' }),
  disclosure_advance_declined: React.createElement(AlertCircle, { size: 16, className: 'text-amber-500' }),
  disclosure_revoked: React.createElement(AlertCircle, { size: 16, className: 'text-red-500' }),
  smpc_verification: React.createElement(Share2, { size: 16, className: 'text-sky-500' }),
  crosspollination_interest: React.createElement(GitBranch, { size: 16, className: 'text-orange-500' }),
  support_ticket_created: React.createElement(LifeBuoy, { size: 16, className: 'text-iflow-500' }),
  support_requester_message: React.createElement(LifeBuoy, { size: 16, className: 'text-iflow-500' }),
  support_admin_reply: React.createElement(LifeBuoy, { size: 16, className: 'text-iflow-500' }),
  support_ticket_closed: React.createElement(LifeBuoy, { size: 16, className: 'text-gray-500' }),
  support_ticket_reopened: React.createElement(LifeBuoy, { size: 16, className: 'text-iflow-500' }),
  support_ticket_escalated: React.createElement(AlertCircle, { size: 16, className: 'text-amber-500' }),
  support_attachment_scan_result: React.createElement(LifeBuoy, { size: 16, className: 'text-iflow-500' }),
  support_quarantine_released: React.createElement(LifeBuoy, { size: 16, className: 'text-green-500' }),
  support_attachment_quarantined: React.createElement(AlertCircle, { size: 16, className: 'text-red-500' }),
}

export function getNotificationNavTarget(n: Notification): string {
  // NB: disclosure types are handled BEFORE honouring n.link on purpose. The
  // backend used to stamp link=/project/{initiating_project_id} on disclosure
  // notifications — the INITIATOR's project, which the recipient cannot access,
  // so the click dead-ended on ProjectView's "not available" → Home root (S2-02).
  switch (n.type) {
    // Support-ticket deep-links (Story #4613, Stream 8b / BUG-102). resource_id is
    // the ticket_id — routed EXPLICITLY to the requester thread, never guessed.
    case 'support_ticket_created':
    case 'support_requester_message':
    case 'support_admin_reply':
    case 'support_ticket_closed':
    case 'support_ticket_reopened':
    case 'support_ticket_escalated':
    case 'support_attachment_scan_result':
    case 'support_quarantine_released':
    case 'support_attachment_quarantined':
      return n.resource_id ? `/support?ticket=${n.resource_id}` : '/support'
    case 'disclosure_advance_request':
    case 'disclosure_consent':
    case 'disclosure_advance_declined':
    case 'disclosure_revoked':
      // resource_id is an AGREEMENT id, not a project id. The recipient acts on
      // the request from Home's pending-disclosure-requests section, which is
      // always accessible and correctly identity-masked per level.
      return '/?view=disclosure-requests'
    case 'crosspollination_interest':
      // here resource_id genuinely IS the partner's project id
      return n.resource_id ? `/project/${n.resource_id}?tab=matches` : '/'
    case 'connection_request':
    case 'connection_response':
      // BUG-102: resource_id here is a connection/request id, NOT a document —
      // routing it to /document/{id} dead-ended at "Document not found". There
      // is no dedicated connections page anymore (/connections redirects to /),
      // so the honest target is the notifications hub.
      return '/notifications'
    default:
      // Defensive: any future support_* type still deep-links to its ticket
      // rather than falling into the document/resource_id guess.
      if (n.type?.startsWith('support_')) {
        return n.resource_id ? `/support?ticket=${n.resource_id}` : '/support'
      }
      if (n.link) return n.link
      return n.resource_id ? `/document/${n.resource_id}` : '/notifications'
  }
}
